import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    View
} from "react-native";
import type {
    CharityBoxItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    DateTimePicker,
    PageContainer,
    SelectBox,
    StateCard,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    PencilIcon,
    TrashIcon,
    PlusIcon,
    BoxIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const CharityBoxes = () => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        width
    } = useWindowDimensions();

    const isMobile = width < 768;

    const [
        charityBoxes,
        setCharityBoxes
    ] = useState<CharityBoxItem[]>([]);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        searchQuery,
        setSearchQuery
    ] = useState("");

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState("");

    const [
        pagination,
        setPagination
    ] = useState({
        totalRecords: 0,
        currentPage: 1,
        totalPages: 1,
        limit: 10
    });

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        previewItem,
        setPreviewItem
    ] = useState<CharityBoxItem | null>(null);

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        formData,
        setFormData
    ] = useState({
        estimated_return_date: "",
        given_date: "",
        member_id: "",
        name: "",
        note: ""
    });

    const [
        receiveModalVisible,
        setReceiveModalVisible
    ] = useState(false);

    const [
        receiveData,
        setReceiveData
    ] = useState({
        return_date: "",
        amount: ""
    });

    const [
        receiveId,
        setReceiveId
    ] = useState<string | null>(null);

    const [
        members,
        setMembers
    ] = useState<{ __title: string; __key: string }[]>([]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPagination(prev => ({
                ...prev,
                currentPage: 1
            }));
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchCharityBoxes = useCallback(async (page: number = 1) => {
        try {
            setIsLoading(true);
            const token = await AsyncStorage.getItem("authToken");
            const url = new URL(`${process.env.API_URL}/charity-boxes`);
            url.searchParams.append("page", page.toString());
            url.searchParams.append("limit", pagination.limit.toString());

            if (debouncedSearch) {
                url.searchParams.append("search", debouncedSearch);
            }

            if (activeOrganizationId && user?.role === "root") {
                url.searchParams.append("organization_id", activeOrganizationId);
            }

            const response = await fetch(url.toString(), {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setCharityBoxes(result.data);
                if (result.totalPages !== undefined) {
                    setPagination({
                        currentPage: result.currentPage,
                        totalRecords: result.totalItems,
                        totalPages: result.totalPages,
                        limit: pagination.limit
                    });
                }
            }
        } catch (error) {
            console.error("Kumbaralar alınamadı:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        activeOrganizationId,
        debouncedSearch,
        user?.role,
        pagination.limit
    ]);

    useEffect(() => {
        if (user?.role === "root" && !activeOrganizationId) {
            setCharityBoxes([]);
            setPagination({
                totalRecords: 0,
                currentPage: 1,
                totalPages: 1,
                limit: 10
            });
            return;
        }

        fetchCharityBoxes(pagination.currentPage);
    }, [
        activeOrganizationId,
        fetchCharityBoxes,
        pagination.currentPage,
        user?.role
    ]);

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const token = await AsyncStorage.getItem("authToken");
                const url = new URL(`${process.env.API_URL}/members`);
                url.searchParams.append("page", "1");
                url.searchParams.append("limit", "1000");

                if (activeOrganizationId && user?.role === "root") {
                    url.searchParams.append("organization_id", activeOrganizationId);
                }

                const response = await fetch(url.toString(), {
                    headers: {
                        ...(activeOrganizationId && user?.role === "root" ? {
                            "x-organization-id": activeOrganizationId
                        } : {}),
                        Authorization: `Bearer ${token}`
                    }
                });
                const result = await response.json();
                if (result.status === "success") {
                    setMembers(result.data.map((m: Record<string, unknown>) => {
                        return {
                            __title: m.name_surname,
                            __key: m.id
                        };
                    }));
                }
            } catch (error) {
                console.error("Üyeler alınamadı:", error);
            }
        };

        if (activeOrganizationId || user?.role !== "root") {
            fetchMembers();
        }
    }, [
        activeOrganizationId,
        user?.role
    ]);

    const handleDelete = async (id: string, name: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const response = await fetch(`${process.env.API_URL}/charity-boxes/${id}`, {
                            headers: {
                                ...(activeOrganizationId && user?.role === "root" ? {
                                    "x-organization-id": activeOrganizationId
                                } : {}),
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });
                        const result = await response.json();
                        if (result.status === "success") {
                            NCoreUIKitToast.open({
                                title: localize("charityBoxes-dialog-delete-title") as string,
                                type: "success"
                            });
                            fetchCharityBoxes(pagination.currentPage);
                        } else {
                            NCoreUIKitToast.open({
                                title: result.message || "Bilinmeyen bir hata oluştu.",
                                type: "danger"
                            });
                        }
                    } catch {
                        NCoreUIKitToast.open({
                            title: "İşlem sırasında bir hata oluştu.",
                            type: "danger"
                        });
                    } finally {
                        isDeleting = false;
                        closeAnimation();
                    }
                },
                title: localize("charityBoxes-dialog-delete-primary"),
                type: "danger"
            },
            content: localize("charityBoxes-dialog-delete-content", [
                name
            ]),
            secondaryButtonProps: {
                title: localize("charityBoxes-dialog-delete-cancel"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            title: localize("charityBoxes-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    const handleSave = async () => {
        try {
            if (!formData.name || !formData.given_date || !formData.member_id) {
                NCoreUIKitToast.open({
                    title: "Lütfen zorunlu alanları (Ad, Veriliş Tarihi, Üye) doldurun.",
                    type: "danger"
                });
                return;
            }

            setIsSubmitting(true);
            const token = await AsyncStorage.getItem("authToken");

            if (editId) {
                const response = await fetch(`${process.env.API_URL}/charity-boxes/${editId}`, {
                    headers: {
                        "Content-Type": "application/json",
                        ...(activeOrganizationId && user?.role === "root" ? {
                            "x-organization-id": activeOrganizationId
                        } : {}),
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(formData),
                    method: "PUT"
                });

                const result = await response.json();

                if (result.status === "success") {
                    NCoreUIKitToast.open({
                        title: localize("charityBoxes-toast-edit-success") as string,
                        type: "success"
                    });
                    setModalVisible(false);
                    fetchCharityBoxes(pagination.currentPage);
                } else {
                    NCoreUIKitToast.open({
                        title: result.message || "Bilinmeyen bir hata oluştu.",
                        type: "danger"
                    });
                }
            } else {
                const response = await fetch(`${process.env.API_URL}/charity-boxes`, {
                    headers: {
                        "Content-Type": "application/json",
                        ...(activeOrganizationId && user?.role === "root" ? {
                            "x-organization-id": activeOrganizationId
                        } : {}),
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(formData),
                    method: "POST"
                });

                const result = await response.json();

                if (result.status === "success") {
                    NCoreUIKitToast.open({
                        title: localize("charityBoxes-toast-create-success") as string,
                        type: "success"
                    });
                    setModalVisible(false);
                    fetchCharityBoxes(pagination.currentPage);
                } else {
                    NCoreUIKitToast.open({
                        title: result.message || "Bilinmeyen bir hata oluştu.",
                        type: "danger"
                    });
                }
            }
        } catch {
            NCoreUIKitToast.open({
                title: "İşlem sırasında bir hata oluştu.",
                type: "danger"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReceive = async () => {
        try {
            if (!receiveData.return_date || !receiveData.amount) {
                NCoreUIKitToast.open({
                    title: "Lütfen teslim alınma tarihi ve tutarı girin.",
                    type: "danger"
                });
                return;
            }

            setIsSubmitting(true);
            const token = await AsyncStorage.getItem("authToken");

            const response = await fetch(`${process.env.API_URL}/charity-boxes/${receiveId}/receive`, {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    amount: parseFloat(receiveData.amount),
                    return_date: receiveData.return_date
                }),
                method: "POST"
            });

            const result = await response.json();

            if (result.status === "success") {
                NCoreUIKitToast.open({
                    title: (result.message as string) || "Başarılı",
                    type: "success"
                });
                setReceiveModalVisible(false);
                fetchCharityBoxes(pagination.currentPage);
            } else {
                NCoreUIKitToast.open({
                    title: result.message || "Bilinmeyen bir hata oluştu.",
                    type: "danger"
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: "İşlem sırasında bir hata oluştu.",
                type: "danger"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const openFormModal = (item?: CharityBoxItem, isReissue: boolean = false) => {
        if (item) {
            setEditId(isReissue ? null : item.id);
            setFormData({
                given_date: isReissue ? new Date().toISOString().split("T")[0] || "" : item.given_date || "",
                estimated_return_date: item.estimated_return_date || "",
                member_id: item.member_id || "",
                name: item.name || "",
                note: item.note || ""
            });
        } else {
            setEditId(null);
            setFormData({
                estimated_return_date: "",
                given_date: "",
                member_id: "",
                name: "",
                note: ""
            });
        }
        setModalVisible(true);
    };

    const openPreviewModal = (item: CharityBoxItem) => {
        setPreviewItem(item);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage
            }));
        }
    };

    const handleJumpPage = (text: string) => {
        const page = parseInt(text);
        if (page && page >= 1 && page <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: page
            }));
        }
    };

    const renderHeader = () => {
        return <View>
            <View
                style={[
                    stylesheet.headerContainer,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("charityBoxes-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchCharityBoxes(pagination.currentPage);
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                    />
                    {
                        (user?.role === "root" || user?.role === "superAdmin" || user?.role === "admin") ?
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => {
                                    return <PlusIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />;
                                }}
                                title={localize("charityBoxes-page-add-button")}
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                onPress={() => openFormModal()}
                                color="primary"
                            />
                        :
                            null
                    }
                </View>
            </View>
            <View
                style={{
                    marginBottom: spaces.spacingXl
                }}
            >
                <TextInput
                    placeholder={localize("charityBoxes-search-placeholder") as string}
                    onChangeText={setSearchQuery}
                    spreadBehaviour="stretch"
                    value={searchQuery}
                />
            </View>
        </View>;
    };

    const renderEmptyState = () => {
        return <StateCard
            subTitle={debouncedSearch ? localize("charityBoxes-list-search-empty-subtitle") : localize("charityBoxes-list-empty-subtitle")}
            title={debouncedSearch ? localize("charityBoxes-list-search-empty-title") : localize("charityBoxes-list-empty-title")}
        />;
    };

    const renderPagination = () => {
        if (pagination.totalPages <= 1) {
            return null;
        }

        return <View
            style={[
                stylesheet.paginationContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <Text
                variant="titleSmallSize"
            >
                Toplam {pagination.totalRecords} kayıt
            </Text>
            <View
                style={[
                    stylesheet.paginationButtonContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    onPress={() => handlePageChange(pagination.currentPage - 1)}
                    title={localize("charityBoxes-pagination-prev")}
                    isDisabled={pagination.currentPage === 1}
                    icon={({
                        color
                    }) => <ChevronLeftIcon
                        color={color}
                        size={16}
                    />}
                    color="secondary"
                    size="small"
                />
                <View
                    style={[
                        stylesheet.paginationJumpContainer,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <TextInput
                        placeholder={pagination.currentPage.toString()}
                        value={pagination.currentPage.toString()}
                        style={stylesheet.paginationJumpInput}
                        onChangeText={handleJumpPage}
                    />
                    <Text
                        variant="titleSmallSize"
                    >
                        / {pagination.totalPages}
                    </Text>
                </View>
                <Button
                    isDisabled={pagination.currentPage === pagination.totalPages}
                    onPress={() => handlePageChange(pagination.currentPage + 1)}
                    title={localize("charityBoxes-pagination-next")}
                    icon={({
                        color
                    }) => <ChevronRightIcon
                        color={color}
                        size={16}
                    />}
                    color="secondary"
                    size="small"
                />
            </View>
        </View>;
    };

    const renderItem = (item: CharityBoxItem, index: number) => {
        return <TouchableOpacity
            style={[
                stylesheet.itemContainer,
                {
                    marginBottom: index === charityBoxes.length - 1 ? 0 : spaces.spacingMd,
                    backgroundColor: colors.content.container.mid,
                    flexDirection: isMobile ? "column" : "row",
                    borderRadius: radiuses.md,
                    padding: spaces.spacingMd,
                    gap: spaces.spacingMd
                }
            ]}
            onPress={() => openPreviewModal(item)}
            activeOpacity={0.7}
            key={item.id}
        >
            <View
                style={[
                    stylesheet.itemContent,
                    {
                        width: isMobile ? "100%" : "auto",
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.itemIcon,
                        {
                            backgroundColor: colors.content.container.default,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <BoxIcon
                        color={colors.content.icon.emphasized}
                        size={24}
                    />
                </View>
                <View
                    style={[
                        stylesheet.itemInfoContainer,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleMediumSize"
                    >
                        {item.name}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {localize("charityBoxes-preview-given-date", [
                            item.given_date
                        ])}
                    </Text>
                </View>
            </View>
            {
                (user?.role === "root" || user?.role === "superAdmin" || user?.role === "admin") ?
                    <View
                        style={[
                            stylesheet.itemActionsContainer,
                            {
                                flexDirection: isMobile ? "column" : "row",
                                width: isMobile ? "100%" : "auto",
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {
                            item.status === "completed" ?
                                <Button
                                    icon={({
                                        color,
                                        size
                                    }) => <BoxIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />}
                                    title={localize("charityBoxes-list-reissue-button") as string}
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    onPress={() => openFormModal(item, true)}
                                    type="neutral"
                                />
                            :
                                <Button
                                    icon={({
                                        color,
                                        size
                                    }) => <BoxIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />}
                                    onPress={() => {
                                        setReceiveId(item.id);
                                        setReceiveData({
                                            return_date: new Date().toISOString().split("T")[0] || "",
                                            amount: ""
                                        });
                                        setReceiveModalVisible(true);
                                    }}
                                    title={localize("charityBoxes-list-receive-button") as string}
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    type="primary"
                                />
                        }
                        <Button
                            icon={({
                                color,
                                size
                            }) => <PencilIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />}
                            title={localize("charityBoxes-list-edit-button")}
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            onPress={() => openFormModal(item)}
                            type="neutral"
                        />
                        <Button
                            icon={({
                                color,
                                size
                            }) => <TrashIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />}
                            title={localize("charityBoxes-list-delete-button")}
                            onPress={() => handleDelete(item.id, item.name)}
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            type="danger"
                        />
                    </View>
                :
                    null
            }
        </TouchableOpacity>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            <View
                style={stylesheet.listContainer}
            >
                {
                    isLoading && charityBoxes.length === 0 ?
                        <ActivityIndicator
                            color={colors.content.icon.default}
                            size="large"
                        />
                    :
                        charityBoxes.length === 0 ? (
                        renderEmptyState()
                    ) : (
                        <>
                            {charityBoxes.map(renderItem)}
                            {renderPagination()}
                        </>)
                }
            </View>
        </View>
        <Modal
            onOverlayPress={({
                closeAnimation
            }: { closeAnimation: (cb: () => void) => void }) => closeAnimation(() => setPreviewItem(null))}
            onClosed={() => setPreviewItem(null)}
            id="charityBox-preview-modal"
            portalName="modal-system"
            isActive={!!previewItem}
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.content.container.default,
                        width: isMobile ? "100%" : 500,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("charityBoxes-preview-title") as string}
                </Text>
                <View
                    style={[
                        stylesheet.formContainer,
                        {
                            marginTop: spaces.spacingMd,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Text>{localize("charityBoxes-preview-name") as string}: {previewItem?.name}</Text>
                    <Text>{localize("charityBoxes-preview-given-date", [
                        previewItem?.given_date || ""
                    ]) as string}</Text>
                    {
                        previewItem?.estimated_return_date ?
                            <Text>{localize("charityBoxes-preview-estimated-return-date", [
                                previewItem.estimated_return_date
                            ])}</Text>
                        :
                            null
                    }
                    <Text>{localize("charityBoxes-preview-member", [
                        previewItem?.member?.name_surname || "Bilinmiyor"
                    ])}</Text>
                    {
                        previewItem?.note ?
                            <Text>{localize("charityBoxes-preview-note")}: {previewItem.note}</Text>
                        :
                            null
                    }
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("charityBoxes-preview-close") as string}
                            onPress={() => setPreviewItem(null)}
                            spreadBehaviour="stretch"
                            type="neutral"
                        />
                    </View>
                </View>
            </View>
        </Modal>
        <Modal
            onOverlayPress={({
                closeAnimation
            }: { closeAnimation: (cb: () => void) => void }) => closeAnimation(() => setModalVisible(false))}
            onClosed={() => setModalVisible(false)}
            id="charityBox-form-modal"
            portalName="modal-system"
            isActive={modalVisible}
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.content.container.default,
                        width: isMobile ? "100%" : 500,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {editId ? localize("charityBoxes-form-title-edit") : localize("charityBoxes-form-title-create")}
                </Text>
                <View
                    style={[
                        stylesheet.formContainer,
                        {
                            marginTop: spaces.spacingMd,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <TextInput
                        placeholder={localize("charityBoxes-form-name-placeholder") as string}
                        title={localize("charityBoxes-form-name-title") as string}
                        onChangeText={(text: string) => setFormData(prev => ({
                            ...prev,
                            name: text
                        }))}
                        defaultValue={formData.name}
                        spreadBehaviour="stretch"
                    />
                    <DateTimePicker
                        initialDate={formData.given_date ? new Date(formData.given_date) : undefined}
                        placeholder={localize("charityBoxes-form-given-date-placeholder") as string}
                        title={localize("charityBoxes-form-given-date-title") as string}
                        onChange={({
                            date
                        }) => setFormData(prev => ({
                            ...prev,
                            given_date: date ? new Date(date).toISOString() : ""
                        }))}
                        dateTimeSheetProps={{
                            portalName: "modal-system"
                        }}
                        spreadBehaviour="stretch"
                    />
                    <SelectBox
                        initialSelectedItems={members.filter(m => m.__key === formData.member_id)}
                        placeholder={localize("charityBoxes-form-member-placeholder") as string}
                        title={localize("charityBoxes-form-member-title") as string}
                        onChange={(selected) => setFormData(prev => ({
                            ...prev,
                            member_id: selected[0]?.__key || ""
                        }))}
                        titleExtractor={(item) => item.__title}
                        keyExtractor={(item) => item.__key}
                        selectSheetProps={{
                            portalName: "modal-system"
                        }}
                        spreadBehaviour="stretch"
                        data={members}
                    />
                    <DateTimePicker
                        initialDate={formData.estimated_return_date ? new Date(formData.estimated_return_date) : undefined}
                        placeholder={localize("charityBoxes-form-estimated-return-date-placeholder") as string}
                        title={localize("charityBoxes-form-estimated-return-date-title") as string}
                        onChange={({
                            date
                        }) => setFormData(prev => ({
                            ...prev,
                            estimated_return_date: date ? new Date(date).toISOString() : ""
                        }))}
                        dateTimeSheetProps={{
                            portalName: "modal-system"
                        }}
                        spreadBehaviour="stretch"
                    />
                    <TextInput
                        placeholder={localize("charityBoxes-form-note-placeholder") as string}
                        title={localize("charityBoxes-form-note-title") as string}
                        onChangeText={(text: string) => setFormData(prev => ({
                            ...prev,
                            note: text
                        }))}
                        defaultValue={formData.note}
                        spreadBehaviour="stretch"
                        multiline={true}
                    />
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("charityBoxes-form-cancel") as string}
                            onPress={() => setModalVisible(false)}
                            spreadBehaviour="stretch"
                            type="neutral"
                        />
                        <Button
                            title={localize(editId ? "charityBoxes-form-update" : "charityBoxes-form-create") as string}
                            spreadBehaviour="stretch"
                            isDisabled={isSubmitting}
                            onPress={handleSave}
                            type="primary"
                        />
                    </View>
                </View>
            </View>
        </Modal>
        <Modal
            onOverlayPress={({
                closeAnimation
            }: { closeAnimation: (cb: () => void) => void }) => closeAnimation(() => setReceiveModalVisible(false))}
            onClosed={() => setReceiveModalVisible(false)}
            isActive={receiveModalVisible}
            id="charityBox-receive-modal"
            portalName="modal-system"
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.content.container.default,
                        width: isMobile ? "100%" : 500,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("charityBoxes-receive-title") as string}
                </Text>
                <View
                    style={[
                        stylesheet.formContainer,
                        {
                            marginTop: spaces.spacingMd,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <DateTimePicker
                        initialDate={receiveData.return_date ? new Date(receiveData.return_date) : undefined}
                        onChange={({
                            date
                        }) => setReceiveData({
                            ...receiveData,
                            return_date: date ? new Date(date).toISOString().split("T")[0] || "" : ""
                        })}
                        title={localize("charityBoxes-form-return-date-title") as string}
                        dateTimeSheetProps={{
                            portalName: "modal-system"
                        }}
                        spreadBehaviour="stretch"
                    />
                    <TextInput
                        placeholder={localize("charityBoxes-form-amount-placeholder") as string}
                        title={localize("charityBoxes-form-amount-title") as string}
                        onChangeText={(text: string) => setReceiveData({
                            ...receiveData,
                            amount: text
                        })}
                        value={receiveData.amount}
                        spreadBehaviour="stretch"
                        keyboardType="numeric"
                    />
                </View>
                <View
                    style={[
                        stylesheet.modalActions,
                        {
                            flexDirection: isMobile ? "column-reverse" : "row",
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        title={localize("charityBoxes-dialog-delete-cancel") as string}
                        onPress={() => setReceiveModalVisible(false)}
                        spreadBehaviour="stretch"
                        type="neutral"
                    />
                    <Button
                        title={localize("charityBoxes-list-receive-button") as string}
                        spreadBehaviour="stretch"
                        isLoading={isSubmitting}
                        onPress={handleReceive}
                        type="primary"
                    />
                </View>
            </View>
        </Modal>
    </PageContainer>;
};

export default CharityBoxes;
